
import React from 'react';
import { Check, X, Clock, UserCheck, CheckSquare, Download } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import Papa from 'papaparse';

interface RosterModuleProps {
  roster: any[];
  attendance: any[];
  onMark: (personId: string, status: string) => void;
  onMarkAllPresent: () => void;
  isPending: boolean;
}

export const RosterModule: React.FC<RosterModuleProps> = ({ roster, attendance, onMark, onMarkAllPresent, isPending }) => {
  const handleExport = () => {
    const data = roster.map(p => {
      const att = attendance.find(a => a.person_id === p.person_id);
      return {
        'First Name': p.people?.first_name,
        'Last Name': p.people?.last_name,
        'Email': p.people?.email || '—',
        'Attendance Status': att?.status || 'pending'
      };
    });
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `session_attendance_${new Date().toISOString().slice(0, 10)}.csv`);
    link.click();
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white">
        <div>
          <h2 className="text-sm font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
            <UserCheck size={16} className="text-emerald-600" /> Attendance Manifest
          </h2>
          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5 italic">
            {roster.length} Personnel Linked to Session
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            className="h-8 px-3 text-[8px]" 
            onClick={handleExport}
            icon={<Download size={12} />}
          >
            Export CSV
          </Button>
          {roster.length > 0 && (
            <Button 
              variant="outline" 
              className="h-8 px-3 text-[8px] border-emerald-100 text-emerald-600 hover:bg-emerald-50" 
              onClick={onMarkAllPresent} 
              isLoading={isPending}
              icon={<CheckSquare size={12} />}
            >
              Mark All Present
            </Button>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 text-[8px] font-black uppercase tracking-[0.2em] select-none">
              <th className="px-6 py-2.5">Persons</th>
              <th className="px-6 py-2.5 text-center">Status Vector</th>
              <th className="px-6 py-2.5 text-right">Verification</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50 bg-white">
            {roster.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-6 py-20 text-center text-[10px] font-black text-slate-200 uppercase tracking-widest italic">
                  Empty Roster Manifest
                </td>
              </tr>
            ) : (
              roster.map((p: any) => {
                const att = attendance.find(a => a.person_id === p.person_id);
                return (
                  <tr key={p.id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="px-6 py-2.5">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 bg-white border border-slate-200 text-slate-900 rounded-sm flex items-center justify-center font-black text-[9px] uppercase shadow-sm group-hover:bg-slate-900 group-hover:text-[#FFDB00] transition-all shrink-0">
                          {p.people?.first_name[0]}{p.people?.last_name[0]}
                        </div>
                        <div className="overflow-hidden">
                          <p className="text-[11px] font-bold text-slate-900 uppercase tracking-tight leading-none mb-0.5 truncate">
                            {p.people?.first_name} {p.people?.last_name}
                          </p>
                          <p className="text-[9px] font-medium text-slate-400 lowercase truncate tracking-tight leading-none">
                            {p.people?.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-2.5">
                      <div className="flex items-center justify-center gap-3">
                        {[
                          { label: 'present', val: 'present', icon: Check, color: 'text-emerald-500 bg-emerald-50 border-emerald-100', active: 'bg-emerald-600 text-white border-emerald-600 shadow-sm scale-105' },
                          { label: 'absent', val: 'absent', icon: X, color: 'text-rose-500 bg-rose-50 border-rose-100', active: 'bg-rose-600 text-white border-rose-600 shadow-sm scale-105' },
                          { label: 'late', val: 'late', icon: Clock, color: 'text-orange-500 bg-orange-50 border-orange-100', active: 'bg-orange-600 text-white border-orange-600 shadow-sm scale-105' }
                        ].map((btn) => (
                          <div key={btn.val} className="flex flex-col items-center gap-1">
                            <button 
                              onClick={() => onMark(p.person_id, btn.val)} 
                              className={`p-1.5 rounded-sm border transition-all ${att?.status === btn.val ? btn.active : `${btn.color} opacity-20 hover:opacity-100`}`}
                            >
                              <btn.icon size={12} strokeWidth={3} />
                            </button>
                            <span className={`text-[7px] font-black uppercase tracking-tighter ${att?.status === btn.val ? 'text-slate-900' : 'text-slate-300'}`}>
                              {btn.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-2.5 text-right">
                       <span className={`text-[9px] font-black uppercase tracking-widest ${att ? 'text-emerald-500' : 'text-slate-200'}`}>
                         {att ? 'verified' : 'pending'}
                       </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
